import { useTranslation } from "react-i18next";
import { AlertCircle, Inbox, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { initials } from "../../utils/format";
export function Avatar({
  name,
  small = false,
  src,
}: {
  name: string;
  small?: boolean;
  src?: string | null;
}) {
  const [failedSrc, setFailedSrc] = useState<string>();
  return (
    <span className={"avatar " + (small ? "small" : "")} aria-hidden="true">
      {src && failedSrc !== src ? <img src={src} alt="" referrerPolicy="no-referrer" onError={() => setFailedSrc(src)} /> : initials(name)}
    </span>
  );
}
export function Badge({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const value = String(children);
  return (
    <span
      className={
        "badge " +
        (["On site", "Received", "Scheduled", "Admin"]
          .flatMap((key) => [key, t(key)])
          .includes(value)
          ? "green"
          : ["Completed", "Delivered", "Checked out", "Cancelled"]
                .flatMap((key) => [key, t(key)])
                .includes(value)
            ? "neutral"
            : "blue")
      }
    >
      <i />
      {typeof children === "string" ? t(children) : children}
    </span>
  );
}
export function ErrorNotice({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="error">
      <AlertCircle size={18} />
      <span>{t(message)}</span>
      {retry && <button onClick={retry}>{t("Try again")}</button>}
    </div>
  );
}
export function Empty({
  title = "Nothing here yet",
  children,
}: {
  title?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="empty">
      <span className="empty-icon">
        <Inbox size={28} />
      </span>
      <h3>{t(title)}</h3>
      <p>{children || t("Your records will appear here.")}</p>
    </div>
  );
}
export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="loading" role="status">
      <span className="spinner" />
      {t("Loading workspace…")}
    </div>
  );
}
export function Modal({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`${wide ? "drawer" : "modal"} ${className}`.trim()}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button
          className="icon-button"
          aria-label={t("Close dialog")}
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
